import {
  IconButton,
  InputAdornment,
  LinearProgress,
  OutlinedInput,
} from "@mui/material";
import { Clear, Search } from "@mui/icons-material";
import { DataGridPro, LicenseInfo, useGridApiRef } from "@mui/x-data-grid-pro";
import {
  GridToolbarContainer,
  GridToolbarColumnsButton,
  GridToolbarFilterButton,
  GridToolbarExport,
  gridClasses,
} from "@mui/x-data-grid-pro";

import { useErrorMessage } from "@poseidon/ui/app/toasts/useErrorMessage";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import ErrorBoundary from "./ErrorBoundary";
import { useSessionStorage } from "usehooks-ts";

LicenseInfo.setLicenseKey(
  "6c8ec9d801ed2161f50e184457d3a475T1JERVI6NDExNDUsRVhQSVJZPTE2ODA3MjM2MDkwMDAsS0VZVkVSU0lPTj0x"
);

function CustomToolbar({ quickFilterValue, onQuickFilterChange }) {
  return (
    <GridToolbarContainer>
      <GridToolbarColumnsButton />
      <GridToolbarFilterButton />
      <GridToolbarExport />
      <OutlinedInput
        size="small"
        placeholder="Search"
        sx={{ ml: "auto", my: 0.5, mr: 0.5, width: 240 }}
        startAdornment={
          <InputAdornment position="start">
            <Search fontSize="small" />
          </InputAdornment>
        }
        endAdornment={
          quickFilterValue ? (
            <InputAdornment position="end">
              <IconButton
                size="small"
                onClick={() => onQuickFilterChange("")}
                edge="end"
              >
                <Clear fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : null
        }
        value={quickFilterValue}
        onChange={(event) => onQuickFilterChange(event.target.value)}
      />
    </GridToolbarContainer>
  );
}

/** Case-insensitive match of the query against any top-level
 * string or number field of the row */
const rowMatchesQuery = (row, query) =>
  Object.values(row).some((value) => {
    if (typeof value === "string") {
      return value.toLowerCase().includes(query);
    }
    if (typeof value === "number") {
      return String(value).includes(query);
    }
    return false;
  });

function DataGrid({
  columns,
  rows,
  fetchRowFunction,
  doubleClickSlug,
  hiddenColumns,
  initialState,
  id,
  useSessionStorageForPagination,
  ...props
}) {
  const [loading, setLoading] = useState();
  const [fetchedRows, setFetchedRows] = useState([]);
  const [filterObject, setFilterValue] = useState();
  // note we only ever pass a static value so even though you should not call hooks conditionally this should be fine
  // todo: ideally we would have a component factory/HOC or two components - or an adapter to avoid this
  // however this is a quick win on usability for now
  const [pageSize, setPageSize] = !useSessionStorageForPagination
    ? // eslint-disable-next-line react-hooks/rules-of-hooks
      useState(50)
    : // eslint-disable-next-line react-hooks/rules-of-hooks
      useSessionStorage(`grid_${id}_pageSize`, 50);
  const [page, setPage] = !useSessionStorageForPagination
    ? // eslint-disable-next-line react-hooks/rules-of-hooks
      useState(0)
    : // eslint-disable-next-line react-hooks/rules-of-hooks
      useSessionStorage(`grid_${id}_page`, 0);
  const [quickFilter, setQuickFilter] = !useSessionStorageForPagination
    ? // eslint-disable-next-line react-hooks/rules-of-hooks
      useState("")
    : // eslint-disable-next-line react-hooks/rules-of-hooks
      useSessionStorage(`grid_${id}_quickFilter`, "");
  const apiRef = useGridApiRef();
  const navigate = useNavigate();
  const { setError } = useErrorMessage();

  useEffect(() => {
    return (
      doubleClickSlug &&
      apiRef.current.subscribeEvent("cellDoubleClick", (params) => {
        const { id } = params;
        navigate(`/${doubleClickSlug}/${id}`);
      })
    );
  }, [apiRef, doubleClickSlug, navigate]);

  const onFilterChange = useCallback(
    (filterModel) =>
      setFilterValue(filterModel.items[0]?.value ? filterModel.items[0] : null),
    []
  );

  const onQuickFilterChange = useCallback(
    (value) => {
      setQuickFilter(value);
      setPage(0);
    },
    [setQuickFilter, setPage]
  );

  /** Automatically hide the ID column and allow
   * other columns to be passed to also be hiddenn */
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({
    id: false,
    ...hiddenColumns?.map((col) => ({ [col]: false })),
  });

  useEffect(() => {
    const loadServerRows = async () => {
      try {
        return (
          await fetchRowFunction({ perPage: pageSize, offset: 0, filterObject })
        ).data;
      } catch (error) {
        // Getting the Error details.
        setError(error.code);
        return [];
      }
    };

    let active = true;

    (async () => {
      if (rows || !fetchRowFunction) {
        return;
      }

      setLoading(true);

      const newRows = await loadServerRows();

      if (!active) {
        return;
      }

      setFetchedRows(newRows);
      setLoading(false);
    })();

    return () => {
      active = false;
    };
  }, [rows, fetchRowFunction, filterObject, pageSize, setError]);

  const allRows = rows || fetchedRows;
  const visibleRows = useMemo(() => {
    if (!quickFilter) {
      return allRows;
    }
    const query = quickFilter.toLowerCase();
    return allRows.filter((row) => rowMatchesQuery(row, query));
  }, [allRows, quickFilter]);

  return (
    <DataGridPro
      initialState={initialState ?? {}}
      components={{
        LoadingOverlay: LinearProgress,
        Toolbar: CustomToolbar,
      }}
      componentsProps={{
        toolbar: {
          quickFilterValue: quickFilter,
          onQuickFilterChange,
        },
      }}
      sx={(theme) => ({
        ...(props.sx ?? {}),
        backgroundColor: "background.paper",
        "&.MuiDataGrid-root ": {
          borderRadius: 0,
          ".MuiDataGrid-row": {
            cursor: "pointer",
            "&:nth-of-type(2n)": {
              backgroundColor:
                theme.palette.type === "light"
                  ? "rgba(235, 235, 235, .3)"
                  : "rgba(235, 235, 235, .3)",
            },
          },
        },
        [`& .${gridClasses.cell}:focus, & .${gridClasses.cell}:focus-within`]: {
          outline: "none",
        },
        [`& .${gridClasses.columnHeader}:focus, & .${gridClasses.columnHeader}:focus-within`]:
          {
            outline: "none",
          },

        border: 0,
        "& .MuiDataGrid-iconSeparator": {
          opacity: 0,
          "&:hover": {
            opacity: 1,
          },
        },
        "& .MuiDataGrid-columnHeaderTitle": {
          fontWeight: "600",
        },
        "& .MuiDataGrid-columnHeaders": {
          borderBottom: 0,
          backgroundColor: "rgb(244, 246, 248)",
          borderRadius: 0,
        },
        "& .MuiDataGrid-row": {
          "& .MuiDataGrid-cell": {
            borderBottom: 0,
          },
          "&:hover": {
            backgroundColor: "rgb(244, 246, 248)",
          },
        },
        "& .MuiDataGrid-columnHeader:not(.MuiDataGrid-columnHeader--sorted) .MuiDataGrid-sortIcon":
          {
            display: "none",
          },
      })}
      pagination
      loading={loading}
      rows={visibleRows}
      columns={columns}
      rowsPerPageOptions={[5, 10, 20, 50, 100]}
      disableSelectionOnClick
      filterMode={fetchRowFunction ? "server" : "client"}
      onFilterModelChange={fetchRowFunction ? onFilterChange : undefined}
      onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
      pageSize={pageSize}
      onPageChange={(newPage) => setPage(newPage)}
      page={page}
      apiRef={apiRef}
      columnVisibilityModel={columnVisibilityModel}
      onColumnVisibilityModelChange={(newModel) =>
        setColumnVisibilityModel(newModel)
      }
      {...props}
    />
  );
}

const ErrorCaughtDataGrid = (props) => (
  <ErrorBoundary>
    <DataGrid {...props} />
  </ErrorBoundary>
);

export default ErrorCaughtDataGrid;
